import React from 'react';
import { ScreenProps } from '../App';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';

const fmt = formatCurrency;

const EmergencyFund: React.FC<ScreenProps> = ({ data, setData }) => {
  const update = (key: keyof typeof data) => (value: any) => setData(prev => ({ ...prev, [key]: value }));

  // Read savings from the unified savings accounts in Assets
  const accounts = data.savingsAccounts?.length ? data.savingsAccounts : [];
  const totalSavings = accounts.reduce((sum, a) => sum + (a.balance || 0), 0);

  // Also include legacy EF accounts if they have balances (backwards compat)
  const legacyEF = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0);
  const totalAccessible = totalSavings + legacyEF;

  const debtMinPayments = data.consumerDebtMonthlyPayment || 0;
  const essentials = (data.mortgagePayment || 0) + debtMinPayments;
  const efMonths = essentials > 0 ? totalAccessible / essentials : 0;
  const efTargetMonths = data.efTargetMonths || 6;
  const efTarget = essentials * efTargetMonths;
  const ef3Target = essentials * 3;
  const pctTarget = efTarget > 0 ? Math.min(totalAccessible / efTarget, 1) * 100 : 0;
  const pct3 = ef3Target > 0 ? Math.min(totalAccessible / ef3Target, 1) * 100 : 0;

  // Monthly top-up for projection
  const monthlyTopUp = data.efTopUp || 0;
  const monthsToTarget = monthlyTopUp > 0 && totalAccessible < efTarget
    ? Math.ceil((efTarget - totalAccessible) / monthlyTopUp)
    : 0;

  // Weighted average interest rate from savings accounts
  const weightedRate = totalSavings > 0
    ? accounts.reduce((sum, a) => sum + (a.balance || 0) * (a.rate || 0), 0) / totalSavings
    : 0;
  const monthlyRate = Math.pow(1 + weightedRate / 100, 1 / 12) - 1;

  // 12-month projection
  const projections: { month: number; balance: number; coverage: number }[] = [];
  let projBal = totalAccessible;
  for (let m = 1; m <= 12; m++) {
    projBal = projBal * (1 + monthlyRate) + monthlyTopUp;
    const cov = essentials > 0 ? projBal / essentials : 0;
    projections.push({ month: m, balance: Math.round(projBal), coverage: cov });
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Emergency Fund</h2>
        <p className="text-gray-500 mt-1 text-sm">
          From your savings accounts in Assets, {efMonths.toFixed(1)} months of committed outgoings are held in accessible savings.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className={`rounded-2xl border-2 p-5 ${efMonths >= efTargetMonths ? 'bg-green-50 border-green-200' : efMonths >= 3 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Coverage</div>
          <div className={`text-3xl font-bold ${efMonths >= efTargetMonths ? 'text-green-700' : efMonths >= 3 ? 'text-amber-700' : 'text-gray-800'}`}>
            {efMonths.toFixed(1)} mo
          </div>
          <div className="text-xs text-gray-400 mt-1">of committed outgoings ({fmt(essentials)}/mo)</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Accessible savings</div>
          <div className="text-3xl font-bold text-gray-800">{fmt(totalAccessible)}</div>
          <div className="text-xs text-gray-400 mt-1">{accounts.length} account{accounts.length !== 1 ? 's' : ''} in Assets</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Target ({efTargetMonths} months)</div>
          <div className="text-3xl font-bold text-gray-800">{fmt(efTarget)}</div>
          <div className="text-xs text-gray-400 mt-1">
            {totalAccessible >= efTarget ? 'Target reached' : monthsToTarget > 0 ? `${monthsToTarget} months to target` : 'Set monthly top-up to project'}
          </div>
        </div>
      </div>

      {/* Savings accounts breakdown (read-only from Assets) */}
      {accounts.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
          <h3 className="text-sm font-semibold text-ma-navy mb-3">Your Savings Accounts</h3>
          <p className="text-xs text-gray-400 mb-3">Entered in Assets — shown here for emergency fund analysis. The adviser will clarify which accounts count as emergency fund during the session.</p>
          <div className="space-y-2">
            {accounts.map((acc, i) => (
              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2.5">
                <div>
                  <div className="text-sm font-medium text-gray-700">{acc.name || `Account ${i + 1}`}</div>
                  {acc.rate > 0 && <div className="text-xs text-gray-400">{acc.rate}% AER</div>}
                </div>
                <div className="text-sm font-bold text-gray-800">{fmt(acc.balance)}</div>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <span className="text-xs text-gray-500 font-medium">Total accessible savings</span>
              <span className="text-sm font-bold text-ma-navy">{fmt(totalAccessible)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Emergency fund target calculation breakdown */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-semibold text-ma-navy mb-3">How the target is calculated</h3>
        {essentials > 0 ? (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Mortgage payment (from Assets)</span>
              <span className="font-medium">{fmt(data.mortgagePayment || 0)}/mo</span>
            </div>
            {debtMinPayments > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Consumer debt minimums (from Assets)</span>
                <span className="font-medium">{fmt(debtMinPayments)}/mo</span>
              </div>
            )}
            <div className="border-t border-gray-100 pt-2 flex justify-between">
              <span className="font-medium text-gray-700">Committed monthly outgoings</span>
              <span className="font-bold text-ma-navy">{fmt(essentials)}/mo</span>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 mt-2 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">3-month target</span>
                <span className="font-medium">{fmt(essentials * 3)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">{efTargetMonths}-month target (recommended for directors)</span>
                <span className="font-bold text-ma-navy">{fmt(efTarget)}</span>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 mt-1">
              <div className="flex justify-between text-xs">
                <span className="text-blue-600">Coverage = {fmt(totalAccessible)} ÷ {fmt(essentials)}</span>
                <span className="font-bold text-blue-700">{efMonths.toFixed(1)} months</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            Enter mortgage and debt details in Assets to calculate emergency fund target.
          </div>
        )}
      </div>

      {/* Progress bars */}
      {essentials > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Progress to target</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>3-month target ({fmt(ef3Target)})</span>
                <span className={pct3 >= 100 ? 'text-green-600 font-medium' : ''}>{pct3.toFixed(0)}%</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: pct3 + '%', backgroundColor: pct3 >= 100 ? '#16a34a' : '#EF9F27' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{efTargetMonths}-month target ({fmt(efTarget)}) — recommended for directors</span>
                <span className={pctTarget >= 100 ? 'text-green-600 font-medium' : ''}>{pctTarget.toFixed(0)}%</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: pctTarget + '%', backgroundColor: pctTarget >= 100 ? '#16a34a' : '#001F8C' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 12-month projection */}
      {monthlyTopUp > 0 && essentials > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="text-sm font-semibold text-gray-700">12-month projection</div>
            <p className="text-xs text-gray-400 mt-0.5">Adding {fmt(monthlyTopUp)}/month at {weightedRate.toFixed(1)}% blended rate across all savings</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">Month</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Balance</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">Coverage</th>
              </tr>
            </thead>
            <tbody>
              {projections.map((p, i) => {
                const hitTarget = p.coverage >= efTargetMonths && (i === 0 || projections[i - 1].coverage < efTargetMonths);
                return (
                  <tr key={i} className={`border-t border-gray-100 ${hitTarget ? 'bg-green-50' : ''}`}>
                    <td className="px-4 py-2.5 text-gray-500">Month {p.month}{hitTarget ? ' ★' : ''}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-800">{fmt(p.balance)}</td>
                    <td className={`px-4 py-2.5 text-right text-xs ${p.coverage >= efTargetMonths ? 'text-green-600 font-medium' : p.coverage >= 3 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {p.coverage.toFixed(1)} mo
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmergencyFund;
